'use client';
import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { Receipt, Send, Clock, CheckCircle, AlertTriangle, Search, Download, X, ChevronDown, ChevronUp, Eye, Bell, FileText, DollarSign, Building2, Home, Warehouse, MapPin, TreePine, Layers, Globe, Printer, Mail, Phone, Hash, ArrowLeft, BarChart2, Settings, CheckSquare, Square, AlertCircle, TrendingUp, Package, Plus } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

// ─── Types ────────────────────────────────────────────────────────────────────

type BillStatus = 'draft' | 'generated' | 'sent' | 'paid' | 'overdue' | 'disputed' | 'waived';
type PropertyType = 'Residential' | 'Commercial' | 'Industrial' | 'Vacant Land' | 'Farmland' | 'Public-Land' | 'Mixed Use' | 'Institutional' | 'Special Economic Zone';
type PaymentTerm = 'full' | 'two_installments' | 'quarterly' | 'monthly';
type BillTemplate = 'standard' | 'commercial' | 'residential' | 'industrial' | 'concession';
type NotifChannel = 'email' | 'sms' | 'portal' | 'postal';
type NotifStatus = 'queued' | 'sending' | 'delivered' | 'failed' | 'bounced';

interface TaxBill {
  billId: string;
  assessmentId: string;
  parcelId: string;
  owner: string;
  taxId: string;
  email: string;
  phone: string;
  address: string;
  county: string;
  propertyType: PropertyType;
  taxYear: number;
  assessedValue: number;
  taxRate: number;
  grossTax: number;
  penalties: number;
  discounts: number;
  finalTax: number;
  dueDate: string;
  paymentTerm: PaymentTerm;
  template: BillTemplate;
  status: BillStatus;
  generatedBy: string;
  generatedDate: string;
  sentDate: string;
  paidDate: string;
  paidAmount: number;
  notes: string;
  notifChannel: NotifChannel;
  notifStatus: NotifStatus | null;
  installments: { label: string; amount: number; dueDate: string; paid: boolean }[];
}

interface NotifQueueItem {
  id: string;
  billId: string;
  owner: string;
  channel: NotifChannel;
  status: NotifStatus;
  scheduledAt: string;
  sentAt: string;
  message: string;
  attempts: number;
}

interface BillTemplate_Config {
  id: BillTemplate;
  name: string;
  description: string;
  propertyTypes: PropertyType[];
  headerColor: string;
  includeMap: boolean;
  includeInstallmentSchedule: boolean;
  includePaymentInstructions: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const BILL_STATUS_CONFIG: Record<BillStatus, { label: string; color: string; bg: string }> = {
  draft:     { label: 'Draft',     color: '#9ca3af', bg: 'rgba(156,163,175,0.12)' },
  generated: { label: 'Generated', color: '#60a5fa', bg: 'rgba(96,165,250,0.12)' },
  sent:      { label: 'Sent',      color: '#a78bfa', bg: 'rgba(167,139,250,0.12)' },
  paid:      { label: 'Paid',      color: '#34d399', bg: 'rgba(52,211,153,0.12)' },
  overdue:   { label: 'Overdue',   color: '#f87171', bg: 'rgba(248,113,113,0.12)' },
  disputed:  { label: 'Disputed',  color: '#fbbf24', bg: 'rgba(251,191,36,0.12)' },
  waived:    { label: 'Waived',    color: '#6b7280', bg: 'rgba(107,114,128,0.12)' },
};

const NOTIF_STATUS_CONFIG: Record<NotifStatus, { label: string; color: string; bg: string }> = {
  queued:    { label: 'Queued',    color: '#94a3b8', bg: 'rgba(148,163,184,0.12)' },
  sending:   { label: 'Sending',   color: '#fbbf24', bg: 'rgba(251,191,36,0.12)' },
  delivered: { label: 'Delivered', color: '#34d399', bg: 'rgba(52,211,153,0.12)' },
  failed:    { label: 'Failed',    color: '#f87171', bg: 'rgba(248,113,113,0.12)' },
  bounced:   { label: 'Bounced',   color: '#fb923c', bg: 'rgba(251,146,60,0.12)' },
};

const PAYMENT_TERM_CONFIG: Record<PaymentTerm, { label: string; description: string }> = {
  full:              { label: 'Full Payment',       description: 'Single payment due on due date' },
  two_installments:  { label: '2 Installments',     description: '50% upfront, 50% in 6 months' },
  quarterly:         { label: 'Quarterly (4x)',      description: '25% each quarter over 12 months' },
  monthly:           { label: 'Monthly (12x)',       description: 'Equal monthly payments over 12 months' },
};

const BILL_TEMPLATES: BillTemplate_Config[] = [
  { id: 'standard',    name: 'Standard LRA Bill',       description: 'Default government tax bill format',          propertyTypes: ['Residential', 'Vacant Land', 'Public-Land', 'Institutional'], headerColor: '#0f1e3d', includeMap: false, includeInstallmentSchedule: true,  includePaymentInstructions: true  },
  { id: 'commercial',  name: 'Commercial Property Bill', description: 'Enhanced format for commercial entities',     propertyTypes: ['Commercial', 'Mixed Use'],                                    headerColor: '#1e3a6e', includeMap: true,  includeInstallmentSchedule: true,  includePaymentInstructions: true  },
  { id: 'residential', name: 'Residential Simplified',  description: 'Simplified format for individual homeowners', propertyTypes: ['Residential'],                                                headerColor: '#162444', includeMap: false, includeInstallmentSchedule: true,  includePaymentInstructions: true  },
  { id: 'industrial',  name: 'Industrial / SEZ Bill',   description: 'Detailed format for industrial properties',   propertyTypes: ['Industrial', 'Special Economic Zone'],                        headerColor: '#0a1628', includeMap: true,  includeInstallmentSchedule: true,  includePaymentInstructions: true  },
  { id: 'concession',  name: 'Concession / Farmland',   description: 'Concession and agricultural land format',     propertyTypes: ['Farmland'],                                                   headerColor: '#0f2010', includeMap: false, includeInstallmentSchedule: false, includePaymentInstructions: true  },
];

const PROP_ICON: Record<PropertyType, React.ReactNode> = {
  Residential: <Home size={12} />,
  Commercial: <Building2 size={12} />,
  Industrial: <Warehouse size={12} />,
  'Vacant Land': <MapPin size={12} />,
  Farmland: <TreePine size={12} />,
  'Public-Land': <Layers size={12} />,
  'Mixed Use': <Layers size={12} />,
  Institutional: <Layers size={12} />,
  'Special Economic Zone': <Globe size={12} />,
};

const fmt = (n: number) => `$${n.toLocaleString()}`;

function buildInstallments(finalTax: number, term: PaymentTerm, dueDate: string): TaxBill['installments'] {
  const base = new Date(dueDate);
  if (term === 'full') return [{ label: 'Full Payment', amount: finalTax, dueDate, paid: false }];
  if (term === 'two_installments') {
    const d2 = new Date(base); d2.setMonth(d2.getMonth() + 6);
    return [
      { label: '1st Installment (50%)', amount: Math.round(finalTax * 0.5), dueDate, paid: false },
      { label: '2nd Installment (50%)', amount: Math.round(finalTax * 0.5), dueDate: d2.toISOString().split('T')[0], paid: false },
    ];
  }
  if (term === 'quarterly') {
    return [0, 3, 6, 9].map((m, i) => {
      const d = new Date(base); d.setMonth(d.getMonth() + m);
      return { label: `Q${i + 1} (25%)`, amount: Math.round(finalTax * 0.25), dueDate: d.toISOString().split('T')[0], paid: false };
    });
  }
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(base); d.setMonth(d.getMonth() + i);
    return { label: `Month ${i + 1}`, amount: Math.round(finalTax / 12), dueDate: d.toISOString().split('T')[0], paid: false };
  });
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const INITIAL_BILLS: TaxBill[] = [
  {
    billId: 'BILL-2026-0001', assessmentId: 'ASS-2026-0004', parcelId: 'LR-NIM-00074',
    owner: 'Nimba Agri Holdings', taxId: 'LRA-TIN-20240074', email: 'accounts@nimbaagri.lr', phone: '+231-770-441-200',
    address: 'Sanniquellie Farm Block C', county: 'Nimba', propertyType: 'Farmland', taxYear: 2026,
    assessedValue: 30000, taxRate: 0.8, grossTax: 240, penalties: 0, discounts: 0, finalTax: 240,
    dueDate: '2026-09-30', paymentTerm: 'full', template: 'concession', status: 'sent',
    generatedBy: 'M. Kollie', generatedDate: '2026-07-05', sentDate: '2026-07-06', paidDate: '', paidAmount: 0,
    notes: 'Customary tenure — verified by Ministry of Agriculture',
    notifChannel: 'email', notifStatus: 'delivered',
    installments: [{ label: 'Full Payment', amount: 240, dueDate: '2026-09-30', paid: false }],
  },
  {
    billId: 'BILL-2026-0002', assessmentId: 'ASS-2026-0009', parcelId: 'LR-MON-00612',
    owner: 'Monrovia Mixed Dev. Ltd.', taxId: 'LRA-TIN-20240612', email: 'finance@monmixed.lr', phone: '+231-886-220-100',
    address: '5 Randall St, Monrovia', county: 'Montserrado', propertyType: 'Mixed Use', taxYear: 2026,
    assessedValue: 850000, taxRate: 2.1, grossTax: 17850, penalties: 0, discounts: 500, finalTax: 17350,
    dueDate: '2026-09-30', paymentTerm: 'quarterly', template: 'commercial', status: 'generated',
    generatedBy: 'J. Jallah', generatedDate: '2026-07-06', sentDate: '', paidDate: '', paidAmount: 0,
    notes: 'High-value mixed development — quarterly payment approved',
    notifChannel: 'email', notifStatus: 'queued',
    installments: buildInstallments(17350, 'quarterly', '2026-09-30'),
  },
  {
    billId: 'BILL-2026-0003', assessmentId: 'ASS-2026-0010', parcelId: 'LR-MAR-00188',
    owner: 'Harbel Residential Trust', taxId: 'LRA-TIN-20240188', email: 'trust@harbel.lr', phone: '+231-770-330-450',
    address: 'Harbel Housing Estate Block 4', county: 'Margibi', propertyType: 'Residential', taxYear: 2026,
    assessedValue: 50000, taxRate: 1.2, grossTax: 600, penalties: 0, discounts: 0, finalTax: 600,
    dueDate: '2026-09-30', paymentTerm: 'two_installments', template: 'residential', status: 'paid',
    generatedBy: 'M. Kollie', generatedDate: '2026-07-02', sentDate: '2026-07-03', paidDate: '2026-07-10', paidAmount: 600,
    notes: 'Paid in full ahead of schedule',
    notifChannel: 'sms', notifStatus: 'delivered',
    installments: [
      { label: '1st Installment (50%)', amount: 300, dueDate: '2026-09-30', paid: true },
      { label: '2nd Installment (50%)', amount: 300, dueDate: '2027-03-30', paid: true },
    ],
  },
  {
    billId: 'BILL-2026-0004', assessmentId: 'ASS-2026-0001', parcelId: 'LR-MON-00421',
    owner: 'Kollie Trading Co.', taxId: 'LRA-TIN-20240421', email: 'tax@kollietrading.lr', phone: '+231-886-100-200',
    address: '14 Broad St, Monrovia', county: 'Montserrado', propertyType: 'Commercial', taxYear: 2026,
    assessedValue: 500000, taxRate: 4.8, grossTax: 24000, penalties: 2400, discounts: 0, finalTax: 26400,
    dueDate: '2026-08-15', paymentTerm: 'two_installments', template: 'commercial', status: 'overdue',
    generatedBy: 'J. Jallah', generatedDate: '2026-06-15', sentDate: '2026-06-16', paidDate: '', paidAmount: 0,
    notes: 'Overdue — penalty applied. Escalated to enforcement.',
    notifChannel: 'email', notifStatus: 'delivered',
    installments: buildInstallments(26400, 'two_installments', '2026-08-15'),
  },
  {
    billId: 'BILL-2026-0005', assessmentId: 'ASS-2026-0005', parcelId: 'LR-MON-00501',
    owner: 'LPA Holdings Ltd.', taxId: 'LRA-TIN-20240501', email: 'legal@lpaholdingsltd.lr', phone: '+231-770-500-600',
    address: '22 Tubman Blvd, Monrovia', county: 'Montserrado', propertyType: 'Vacant Land', taxYear: 2026,
    assessedValue: 195000, taxRate: 3.2, grossTax: 6240, penalties: 0, discounts: 1872, finalTax: 4368,
    dueDate: '2026-09-30', paymentTerm: 'full', template: 'standard', status: 'disputed',
    generatedBy: 'J. Jallah', generatedDate: '2026-07-04', sentDate: '2026-07-05', paidDate: '', paidAmount: 0,
    notes: 'Dispute filed — court case pending. Bill on hold.',
    notifChannel: 'portal', notifStatus: 'delivered',
    installments: [{ label: 'Full Payment', amount: 4368, dueDate: '2026-09-30', paid: false }],
  },
  {
    billId: 'BILL-2026-0006', assessmentId: 'ASS-2026-0007', parcelId: 'LR-LOF-00055',
    owner: 'Lofa Timber Ltd.', taxId: 'LRA-TIN-20240055', email: 'accounts@lofatimber.lr', phone: '+231-886-770-330',
    address: 'Voinjama Logging Concession', county: 'Lofa', propertyType: 'Farmland', taxYear: 2026,
    assessedValue: 50000, taxRate: 0.8, grossTax: 400, penalties: 0, discounts: 0, finalTax: 400,
    dueDate: '2026-09-30', paymentTerm: 'full', template: 'concession', status: 'draft',
    generatedBy: '', generatedDate: '', sentDate: '', paidDate: '', paidAmount: 0,
    notes: 'Concession agreement expires 2030',
    notifChannel: 'email', notifStatus: null,
    installments: [{ label: 'Full Payment', amount: 400, dueDate: '2026-09-30', paid: false }],
  },
];

const INITIAL_NOTIF_QUEUE: NotifQueueItem[] = [
  { id: 'NQ-001', billId: 'BILL-2026-0002', owner: 'Monrovia Mixed Dev. Ltd.', channel: 'email', status: 'queued', scheduledAt: '2026-07-08 18:00', sentAt: '', message: 'Tax Bill BILL-2026-0002 for FY2026 — Amount Due: $17,350. Due Date: 30 Sep 2026. Quarterly payment plan active.', attempts: 0 },
  { id: 'NQ-002', billId: 'BILL-2026-0001', owner: 'Nimba Agri Holdings', channel: 'email', status: 'delivered', scheduledAt: '2026-07-06 09:00', sentAt: '2026-07-06 09:02', message: 'Tax Bill BILL-2026-0001 for FY2026 — Amount Due: $240. Due Date: 30 Sep 2026.', attempts: 1 },
  { id: 'NQ-003', billId: 'BILL-2026-0003', owner: 'Harbel Residential Trust', channel: 'sms', status: 'delivered', scheduledAt: '2026-07-03 10:00', sentAt: '2026-07-03 10:01', message: 'LRA Tax Bill BILL-2026-0003: $600 due 30 Sep 2026. 2-installment plan. Pay at lra.gov.lr', attempts: 1 },
  { id: 'NQ-004', billId: 'BILL-2026-0004', owner: 'Kollie Trading Co.', channel: 'email', status: 'delivered', scheduledAt: '2026-06-16 09:00', sentAt: '2026-06-16 09:03', message: 'OVERDUE NOTICE: Tax Bill BILL-2026-0004 — $26,400 was due 15 Aug 2026. Penalty applied. Immediate payment required.', attempts: 1 },
  { id: 'NQ-005', billId: 'BILL-2026-0005', owner: 'LPA Holdings Ltd.', channel: 'portal', status: 'delivered', scheduledAt: '2026-07-05 11:00', sentAt: '2026-07-05 11:01', message: 'Tax Bill BILL-2026-0005 available on your portal. Dispute filed — bill on hold pending court resolution.', attempts: 1 },
  { id: 'NQ-006', billId: 'BILL-2026-0004', owner: 'Kollie Trading Co.', channel: 'sms', status: 'failed', scheduledAt: '2026-07-07 09:00', sentAt: '', message: 'FINAL NOTICE: Tax Bill BILL-2026-0004 overdue. Contact LRA immediately.', attempts: 3 },
];

// ─── Pending Assessments (from Stage 5) ──────────────────────────────────────

interface PendingAssessment {
  assessmentId: string;
  parcelId: string;
  owner: string;
  taxId: string;
  email: string;
  phone: string;
  address: string;
  county: string;
  propertyType: PropertyType;
  assessedValue: number;
  finalTax: number;
  approvedDate: string;
  approvedBy: string;
}

const PENDING_ASSESSMENTS: PendingAssessment[] = [
  { assessmentId: 'ASS-2026-0002', parcelId: 'LR-MON-00388', owner: 'Fatima Kamara', taxId: 'LRA-TIN-20240388', email: 'fatima.kamara@email.lr', phone: '+231-770-388-100', address: '7 Lynch St, Sinkor', county: 'Montserrado', propertyType: 'Residential', assessedValue: 70000, finalTax: 840, approvedDate: '2026-07-05', approvedBy: 'J. Jallah' },
  { assessmentId: 'ASS-2026-0003', parcelId: 'LR-MAR-00112', owner: 'Margibi Industrial Corp.', taxId: 'LRA-TIN-20240112', email: 'finance@margibiind.lr', phone: '+231-886-112-300', address: 'Free Port Industrial Zone', county: 'Margibi', propertyType: 'Industrial', assessedValue: 1400000, finalTax: 56000, approvedDate: '2026-07-06', approvedBy: 'T. Freeman' },
  { assessmentId: 'ASS-2026-0008', parcelId: 'LR-GBA-00143', owner: 'Grand Bassa Realty', taxId: 'LRA-TIN-20240143', email: 'info@grandbassarealty.lr', phone: '+231-770-143-500', address: 'Buchanan Waterfront Plot', county: 'Grand Bassa', propertyType: 'Residential', assessedValue: 130000, finalTax: 1560, approvedDate: '2026-07-07', approvedBy: 'M. Kollie' },
];

// ─── Batch Bill Generation Modal ──────────────────────────────────────────────

function BatchGenerateModal({
  assessments,
  onGenerate,
  onClose,
}: {
  assessments: PendingAssessment[];
  onGenerate: (selected: string[], config: { dueDate: string; paymentTerm: PaymentTerm; template: BillTemplate; notifChannel: NotifChannel; notes: string }) => void;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<string[]>(assessments.map(a => a.assessmentId));
  const [dueDate, setDueDate] = useState('2026-09-30');
  const [paymentTerm, setPaymentTerm] = useState<PaymentTerm>('full');
  const [template, setTemplate] = useState<BillTemplate>('standard');
  const [notifChannel, setNotifChannel] = useState<NotifChannel>('email');
  const [notes, setNotes] = useState('');
  const [step, setStep] = useState<1 | 2 | 3>(1);

  const toggleAll = () => setSelected(selected.length === assessments.length ? [] : assessments.map(a => a.assessmentId));
  const toggle = (id: string) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const selectedAssessments = assessments.filter(a => selected.includes(a.assessmentId));
  const totalTax = selectedAssessments.reduce((s, a) => s + a.finalTax, 0);

  const inputCls = 'w-full px-3 py-2 rounded-lg text-sm outline-none focus:ring-1 focus:ring-yellow-500/40 transition-all';
  const inputStyle = { backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid var(--dark-border)', color: 'var(--dark-text)' };
  const labelCls = 'block text-xs font-medium mb-1.5';
  const labelStyle = { color: 'var(--dark-muted)' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-2xl rounded-2xl overflow-hidden flex flex-col" style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)', maxHeight: '92vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: 'var(--dark-border)' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(212,160,23,0.15)' }}>
              <Receipt size={15} style={{ color: 'var(--accent)' }} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: 'rgba(212,160,23,0.15)', color: 'var(--accent)' }}>STAGE 6</span>
                <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>Batch Bill Generation</span>
              </div>
              <h3 className="font-bold text-sm mt-0.5" style={{ color: 'var(--dark-text)' }}>Generate Tax Bills from Stage 5 Assessments</h3>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5 transition-colors" style={{ color: 'var(--dark-muted)' }}><X size={16} /></button>
        </div>

        {/* Step Tabs */}
        <div className="flex border-b flex-shrink-0" style={{ borderColor: 'var(--dark-border)' }}>
          {[{ n: 1 as const, label: 'Select Assessments' }, { n: 2 as const, label: 'Configure Bill' }, { n: 3 as const, label: 'Review & Generate' }].map(s => (
            <button key={s.n} onClick={() => { if (s.n < step || (s.n === 2 && selected.length > 0) || (s.n === 3 && selected.length > 0)) setStep(s.n); }}
              className="flex-1 py-3 text-xs font-medium transition-all"
              style={{
                color: step === s.n ? 'var(--accent)' : 'var(--dark-muted)',
                borderBottom: `2px solid ${step === s.n ? 'var(--accent)' : 'transparent'}`,
                backgroundColor: step === s.n ? 'rgba(212,160,23,0.05)' : 'transparent',
              }}>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-4 h-4 rounded-full text-xs flex items-center justify-center font-bold"
                  style={{ backgroundColor: step >= s.n ? 'rgba(212,160,23,0.2)' : 'rgba(255,255,255,0.05)', color: step >= s.n ? 'var(--accent)' : 'var(--dark-muted)' }}>
                  {s.n}
                </span>
                {s.label}
              </span>
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Select */}
          {step === 1 && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{assessments.length} approved assessments ready for billing</p>
                <button onClick={toggleAll} className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors hover:bg-white/5" style={{ color: 'var(--accent)', border: '1px solid rgba(212,160,23,0.3)' }}>
                  {selected.length === assessments.length ? <><CheckSquare size={12} /> Deselect All</> : <><Square size={12} /> Select All</>}
                </button>
              </div>
              <div className="space-y-2">
                {assessments.map(a => (
                  <div key={a.assessmentId} onClick={() => toggle(a.assessmentId)}
                    className="flex items-start gap-3 p-3 rounded-xl cursor-pointer transition-all hover:bg-white/5"
                    style={{ border: `1px solid ${selected.includes(a.assessmentId) ? 'rgba(212,160,23,0.3)' : 'var(--dark-border)'}`, backgroundColor: selected.includes(a.assessmentId) ? 'rgba(212,160,23,0.05)' : 'transparent' }}>
                    <div className="mt-0.5 flex-shrink-0">
                      {selected.includes(a.assessmentId)
                        ? <CheckSquare size={15} style={{ color: 'var(--accent)' }} />
                        : <Square size={15} style={{ color: 'var(--dark-muted)' }} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold truncate" style={{ color: 'var(--dark-text)' }}>{a.owner}</span>
                        <span className="text-sm font-bold flex-shrink-0" style={{ color: '#34d399' }}>{fmt(a.finalTax)}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>{a.parcelId}</span>
                        <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>·</span>
                        <span className="text-xs flex items-center gap-1" style={{ color: 'var(--dark-muted)' }}>{PROP_ICON[a.propertyType]} {a.propertyType}</span>
                        <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>·</span>
                        <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>{a.county}</span>
                      </div>
                      <div className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>Approved by {a.approvedBy} on {a.approvedDate}</div>
                    </div>
                  </div>
                ))}
              </div>
              {selected.length > 0 && (
                <div className="mt-4 p-3 rounded-xl flex items-center justify-between" style={{ backgroundColor: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.2)' }}>
                  <span className="text-xs font-medium" style={{ color: '#34d399' }}>{selected.length} assessment{selected.length > 1 ? 's' : ''} selected</span>
                  <span className="text-sm font-bold" style={{ color: '#34d399' }}>Total: {fmt(totalTax)}</span>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Configure */}
          {step === 2 && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls} style={labelStyle}>Due Date *</label>
                  <input type="date" className={inputCls} style={inputStyle} value={dueDate} onChange={e => setDueDate(e.target.value)} />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Notification Channel</label>
                  <select className={inputCls} style={inputStyle} value={notifChannel} onChange={e => setNotifChannel(e.target.value as NotifChannel)}>
                    <option value="email">Email</option>
                    <option value="sms">SMS</option>
                    <option value="portal">Taxpayer Portal</option>
                    <option value="postal">Postal Mail</option>
                  </select>
                </div>
              </div>

              <div>
                <label className={labelCls} style={labelStyle}>Payment Terms</label>
                <div className="grid grid-cols-2 gap-2">
                  {(Object.entries(PAYMENT_TERM_CONFIG) as [PaymentTerm, { label: string; description: string }][]).map(([key, cfg]) => (
                    <button key={key} onClick={() => setPaymentTerm(key)}
                      className="p-3 rounded-xl text-left transition-all"
                      style={{
                        border: `1px solid ${paymentTerm === key ? 'rgba(212,160,23,0.4)' : 'var(--dark-border)'}`,
                        backgroundColor: paymentTerm === key ? 'rgba(212,160,23,0.08)' : 'rgba(255,255,255,0.03)',
                      }}>
                      <div className="text-xs font-semibold mb-0.5" style={{ color: paymentTerm === key ? 'var(--accent)' : 'var(--dark-text)' }}>{cfg.label}</div>
                      <div className="text-xs" style={{ color: 'var(--dark-muted)' }}>{cfg.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className={labelCls} style={labelStyle}>Bill Template</label>
                <div className="space-y-2">
                  {BILL_TEMPLATES.map(t => (
                    <button key={t.id} onClick={() => setTemplate(t.id)}
                      className="w-full p-3 rounded-xl text-left flex items-center gap-3 transition-all"
                      style={{
                        border: `1px solid ${template === t.id ? 'rgba(212,160,23,0.4)' : 'var(--dark-border)'}`,
                        backgroundColor: template === t.id ? 'rgba(212,160,23,0.08)' : 'rgba(255,255,255,0.03)',
                      }}>
                      <div className="w-8 h-8 rounded-lg flex-shrink-0" style={{ backgroundColor: t.headerColor, border: '1px solid rgba(255,255,255,0.1)' }} />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold" style={{ color: template === t.id ? 'var(--accent)' : 'var(--dark-text)' }}>{t.name}</div>
                        <div className="text-xs" style={{ color: 'var(--dark-muted)' }}>{t.description}</div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        {t.includeMap && <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(96,165,250,0.1)', color: '#60a5fa' }}>Map</span>}
                        {t.includeInstallmentSchedule && <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(167,139,250,0.1)', color: '#a78bfa' }}>Schedule</span>}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className={labelCls} style={labelStyle}>Additional Notes</label>
                <textarea rows={3} className={inputCls} style={inputStyle} value={notes} onChange={e => setNotes(e.target.value)}
                  placeholder="Optional notes to include on all generated bills…" />
              </div>
            </div>
          )}

          {/* Step 3: Review */}
          {step === 3 && (
            <div>
              <div className="p-4 rounded-xl mb-4" style={{ backgroundColor: 'rgba(212,160,23,0.06)', border: '1px solid rgba(212,160,23,0.2)' }}>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <div className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>Bills to Generate</div>
                    <div className="text-xl font-bold" style={{ color: 'var(--accent)' }}>{selected.length}</div>
                  </div>
                  <div>
                    <div className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>Total Tax Due</div>
                    <div className="text-xl font-bold" style={{ color: '#34d399' }}>{fmt(totalTax)}</div>
                  </div>
                  <div>
                    <div className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>Due Date</div>
                    <div className="text-sm font-bold" style={{ color: 'var(--dark-text)' }}>{dueDate}</div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 mt-3 pt-3" style={{ borderTop: '1px solid rgba(212,160,23,0.15)' }}>
                  <div>
                    <div className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>Payment Terms</div>
                    <div className="text-xs font-semibold" style={{ color: 'var(--dark-text)' }}>{PAYMENT_TERM_CONFIG[paymentTerm].label}</div>
                  </div>
                  <div>
                    <div className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>Template</div>
                    <div className="text-xs font-semibold" style={{ color: 'var(--dark-text)' }}>{BILL_TEMPLATES.find(t => t.id === template)?.name}</div>
                  </div>
                  <div>
                    <div className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>Notification</div>
                    <div className="text-xs font-semibold capitalize" style={{ color: 'var(--dark-text)' }}>{notifChannel}</div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                {selectedAssessments.map(a => (
                  <div key={a.assessmentId} className="flex items-center justify-between p-3 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid var(--dark-border)' }}>
                    <div>
                      <div className="text-sm font-medium" style={{ color: 'var(--dark-text)' }}>{a.owner}</div>
                      <div className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>{a.parcelId} · {a.county}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold" style={{ color: '#34d399' }}>{fmt(a.finalTax)}</div>
                      <div className="text-xs" style={{ color: 'var(--dark-muted)' }}>Assessed: {fmt(a.assessedValue)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t flex-shrink-0" style={{ borderColor: 'var(--dark-border)' }}>
          <button onClick={() => step > 1 ? setStep((step - 1) as 1 | 2 | 3) : onClose()}
            className="px-4 py-2 rounded-lg text-sm border transition-colors hover:bg-white/5"
            style={{ borderColor: 'var(--dark-border)', color: 'var(--dark-muted)' }}>
            {step === 1 ? 'Cancel' : '← Back'}
          </button>
          {step < 3 ? (
            <button onClick={() => setStep((step + 1) as 2 | 3)}
              disabled={step === 1 && selected.length === 0}
              className="px-5 py-2 rounded-lg text-sm font-semibold transition-all"
              style={{
                backgroundColor: selected.length > 0 ? 'var(--accent)' : 'rgba(212,160,23,0.2)',
                color: selected.length > 0 ? '#000' : '#9ca3af',
                cursor: selected.length > 0 ? 'pointer' : 'not-allowed',
              }}>
              Continue →
            </button>
          ) : (
            <button onClick={() => onGenerate(selected, { dueDate, paymentTerm, template, notifChannel, notes })}
              className="px-5 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all"
              style={{ backgroundColor: 'var(--accent)', color: '#000' }}>
              <Receipt size={14} /> Generate {selected.length} Bill{selected.length > 1 ? 's' : ''}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Bill Detail Modal ────────────────────────────────────────────────────────

function BillDetailModal({ bill, onClose, onSend }: { bill: TaxBill; onClose: () => void; onSend: (billId: string) => void }) {
  const [tab, setTab] = useState<'overview' | 'installments' | 'notif'>('overview');
  const sc = BILL_STATUS_CONFIG[bill.status];
  const tpl = BILL_TEMPLATES.find(t => t.id === bill.template);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-2xl rounded-2xl overflow-hidden flex flex-col" style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)', maxHeight: '90vh' }}>
        {/* Header */}
        <div className="px-5 py-4 border-b flex-shrink-0" style={{ borderColor: 'var(--dark-border)' }}>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-mono font-bold" style={{ color: 'var(--accent)' }}>{bill.billId}</span>
                <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ color: sc.color, backgroundColor: sc.bg }}>{sc.label}</span>
              </div>
              <h3 className="font-bold text-base" style={{ color: 'var(--dark-text)' }}>{bill.owner}</h3>
              <div className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>{bill.parcelId} · {bill.address}</div>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5 transition-colors" style={{ color: 'var(--dark-muted)' }}><X size={16} /></button>
          </div>
          <div className="flex gap-1 mt-3">
            {(['overview', 'installments', 'notif'] as const).map(t => (
              <button key={t} onClick={() => setTab(t)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all capitalize"
                style={{
                  backgroundColor: tab === t ? 'rgba(212,160,23,0.12)' : 'transparent',
                  color: tab === t ? 'var(--accent)' : 'var(--dark-muted)',
                  border: `1px solid ${tab === t ? 'rgba(212,160,23,0.3)' : 'transparent'}`,
                }}>
                {t === 'notif' ? 'Notifications' : t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {tab === 'overview' && (
            <div className="space-y-4">
              {/* Tax Summary */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Assessed Value', value: fmt(bill.assessedValue), color: '#60a5fa' },
                  { label: 'Gross Tax', value: fmt(bill.grossTax), color: 'var(--dark-text)' },
                  { label: 'Penalties', value: fmt(bill.penalties), color: bill.penalties > 0 ? '#f87171' : 'var(--dark-muted)' },
                  { label: 'Discounts', value: `−${fmt(bill.discounts)}`, color: bill.discounts > 0 ? '#34d399' : 'var(--dark-muted)' },
                ].map(item => (
                  <div key={item.label} className="p-3 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid var(--dark-border)' }}>
                    <div className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>{item.label}</div>
                    <div className="text-base font-bold" style={{ color: item.color }}>{item.value}</div>
                  </div>
                ))}
              </div>
              <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.2)' }}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold" style={{ color: '#34d399' }}>Final Tax Due</span>
                  <span className="text-2xl font-bold" style={{ color: '#34d399' }}>{fmt(bill.finalTax)}</span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>Due Date</span>
                  <span className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>{bill.dueDate}</span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>Payment Terms</span>
                  <span className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>{PAYMENT_TERM_CONFIG[bill.paymentTerm].label}</span>
                </div>
              </div>
              {/* Property & Contact */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-xl space-y-2" style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid var(--dark-border)' }}>
                  <div className="text-xs font-semibold mb-2" style={{ color: 'var(--dark-muted)' }}>PROPERTY</div>
                  <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--dark-text)' }}>{PROP_ICON[bill.propertyType]} {bill.propertyType}</div>
                  <div className="text-xs" style={{ color: 'var(--dark-muted)' }}>{bill.county} County</div>
                  <div className="text-xs" style={{ color: 'var(--dark-muted)' }}>Tax Year: {bill.taxYear}</div>
                  <div className="text-xs" style={{ color: 'var(--dark-muted)' }}>Template: {tpl?.name}</div>
                </div>
                <div className="p-3 rounded-xl space-y-2" style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid var(--dark-border)' }}>
                  <div className="text-xs font-semibold mb-2" style={{ color: 'var(--dark-muted)' }}>CONTACT</div>
                  <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--dark-text)' }}><Mail size={10} /> {bill.email}</div>
                  <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--dark-text)' }}><Phone size={10} /> {bill.phone}</div>
                  <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--dark-text)' }}><Hash size={10} /> {bill.taxId}</div>
                </div>
              </div>
              {bill.notes && (
                <div className="p-3 rounded-xl" style={{ backgroundColor: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.2)' }}>
                  <div className="text-xs font-semibold mb-1" style={{ color: '#fbbf24' }}>Notes</div>
                  <div className="text-xs" style={{ color: 'var(--dark-muted)' }}>{bill.notes}</div>
                </div>
              )}
            </div>
          )}

          {tab === 'installments' && (
            <div>
              <div className="text-xs mb-3" style={{ color: 'var(--dark-muted)' }}>
                {PAYMENT_TERM_CONFIG[bill.paymentTerm].label} — {PAYMENT_TERM_CONFIG[bill.paymentTerm].description}
              </div>
              <div className="space-y-2">
                {bill.installments.map((inst, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-xl"
                    style={{ backgroundColor: inst.paid ? 'rgba(52,211,153,0.06)' : 'rgba(255,255,255,0.03)', border: `1px solid ${inst.paid ? 'rgba(52,211,153,0.2)' : 'var(--dark-border)'}` }}>
                    <div className="flex items-center gap-2">
                      {inst.paid ? <CheckCircle size={14} style={{ color: '#34d399' }} /> : <Clock size={14} style={{ color: 'var(--dark-muted)' }} />}
                      <div>
                        <div className="text-xs font-medium" style={{ color: 'var(--dark-text)' }}>{inst.label}</div>
                        <div className="text-xs" style={{ color: 'var(--dark-muted)' }}>Due: {inst.dueDate}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold" style={{ color: inst.paid ? '#34d399' : 'var(--dark-text)' }}>{fmt(inst.amount)}</div>
                      {inst.paid && <div className="text-xs" style={{ color: '#34d399' }}>Paid</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'notif' && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs" style={{ color: 'var(--dark-muted)' }}>Notification channel: <span className="capitalize font-medium" style={{ color: 'var(--dark-text)' }}>{bill.notifChannel}</span></div>
                {bill.notifStatus && (
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ color: NOTIF_STATUS_CONFIG[bill.notifStatus].color, backgroundColor: NOTIF_STATUS_CONFIG[bill.notifStatus].bg }}>
                    {NOTIF_STATUS_CONFIG[bill.notifStatus].label}
                  </span>
                )}
              </div>
              {bill.status === 'generated' && (
                <div className="p-4 rounded-xl mb-4" style={{ backgroundColor: 'rgba(96,165,250,0.06)', border: '1px solid rgba(96,165,250,0.2)' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Bell size={14} style={{ color: '#60a5fa' }} />
                    <span className="text-xs font-semibold" style={{ color: '#60a5fa' }}>Ready to Send</span>
                  </div>
                  <p className="text-xs mb-3" style={{ color: 'var(--dark-muted)' }}>This bill has been generated and is ready to be sent to the taxpayer via {bill.notifChannel}.</p>
                  <button onClick={() => { onSend(bill.billId); onClose(); }}
                    className="w-full py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2"
                    style={{ backgroundColor: 'var(--accent)', color: '#000' }}>
                    <Send size={13} /> Send Bill Now
                  </button>
                </div>
              )}
              <div className="p-3 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid var(--dark-border)' }}>
                <div className="text-xs font-semibold mb-2" style={{ color: 'var(--dark-muted)' }}>NOTIFICATION PREVIEW</div>
                <div className="text-xs leading-relaxed" style={{ color: 'var(--dark-text)' }}>
                  Dear {bill.owner},<br /><br />
                  Your property tax bill ({bill.billId}) for FY{bill.taxYear} has been generated by the Liberia Revenue Authority.<br /><br />
                  <strong>Amount Due:</strong> {fmt(bill.finalTax)}<br />
                  <strong>Due Date:</strong> {bill.dueDate}<br />
                  <strong>Payment Terms:</strong> {PAYMENT_TERM_CONFIG[bill.paymentTerm].label}<br /><br />
                  Please log in to the LRA Taxpayer Portal at lra.gov.lr to view and pay your bill.<br /><br />
                  Liberia Revenue Authority
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-2 px-5 py-4 border-t flex-shrink-0" style={{ borderColor: 'var(--dark-border)' }}>
          <button onClick={onClose} className="flex-1 py-2 rounded-lg text-sm border transition-colors hover:bg-white/5" style={{ borderColor: 'var(--dark-border)', color: 'var(--dark-muted)' }}>Close</button>
          {bill.status === 'generated' && (
            <button onClick={() => { onSend(bill.billId); onClose(); }}
              className="flex-1 py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2"
              style={{ backgroundColor: 'var(--accent)', color: '#000' }}>
              <Send size={13} /> Send to Taxpayer
            </button>
          )}
          <button className="px-4 py-2 rounded-lg text-sm flex items-center gap-1.5 transition-colors hover:bg-white/5" style={{ border: '1px solid var(--dark-border)', color: 'var(--dark-muted)' }}>
            <Printer size={13} /> Print
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

type TabId = 'bills' | 'generate' | 'templates' | 'notifications' | 'analytics';

export default function BillingDashboardClient() {
  const [activeTab, setActiveTab] = useState<TabId>('bills');
  const [bills, setBills] = useState<TaxBill[]>(INITIAL_BILLS);
  const [notifQueue, setNotifQueue] = useState<NotifQueueItem[]>(INITIAL_NOTIF_QUEUE);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<BillStatus | 'all'>('all');
  const [countyFilter, setCountyFilter] = useState('all');
  const [selectedBills, setSelectedBills] = useState<string[]>([]);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [detailBill, setDetailBill] = useState<TaxBill | null>(null);
  const [sortField, setSortField] = useState<'billId' | 'owner' | 'finalTax' | 'dueDate' | 'status'>('billId');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const counties = useMemo(() => ['all', ...Array.from(new Set(bills.map(b => b.county)))], [bills]);

  const filtered = useMemo(() => {
    let list = [...bills];
    if (search) list = list.filter(b => b.owner.toLowerCase().includes(search.toLowerCase()) || b.billId.toLowerCase().includes(search.toLowerCase()) || b.parcelId.toLowerCase().includes(search.toLowerCase()));
    if (statusFilter !== 'all') list = list.filter(b => b.status === statusFilter);
    if (countyFilter !== 'all') list = list.filter(b => b.county === countyFilter);
    list.sort((a, b) => {
      let av: string | number = a[sortField] as string | number;
      let bv: string | number = b[sortField] as string | number;
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      return sortDir === 'asc' ? (av < bv ? -1 : av > bv ? 1 : 0) : (av > bv ? -1 : av < bv ? 1 : 0);
    });
    return list;
  }, [bills, search, statusFilter, countyFilter, sortField, sortDir]);

  const kpis = useMemo(() => {
    const total = bills.length;
    const totalTax = bills.reduce((s, b) => s + b.finalTax, 0);
    const collected = bills.filter(b => b.status === 'paid').reduce((s, b) => s + b.paidAmount, 0);
    const overdue = bills.filter(b => b.status === 'overdue').length;
    const pending = bills.filter(b => b.status === 'generated' || b.status === 'sent').length;
    const queued = notifQueue.filter(n => n.status === 'queued').length;
    return { total, totalTax, collected, overdue, pending, queued };
  }, [bills, notifQueue]);

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const toggleSelectAll = () => setSelectedBills(selectedBills.length === filtered.length ? [] : filtered.map(b => b.billId));
  const toggleSelect = (id: string) => setSelectedBills(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const handleGenerate = (selected: string[], config: { dueDate: string; paymentTerm: PaymentTerm; template: BillTemplate; notifChannel: NotifChannel; notes: string }) => {
    const now = new Date().toISOString().split('T')[0];
    const newBills: TaxBill[] = PENDING_ASSESSMENTS.filter(a => selected.includes(a.assessmentId)).map((a, i) => {
      const billId = `BILL-2026-${String(bills.length + i + 1).padStart(4, '0')}`;
      return {
        billId, assessmentId: a.assessmentId, parcelId: a.parcelId,
        owner: a.owner, taxId: a.taxId, email: a.email, phone: a.phone,
        address: a.address, county: a.county, propertyType: a.propertyType,
        taxYear: 2026, assessedValue: a.assessedValue, taxRate: 0,
        grossTax: a.finalTax, penalties: 0, discounts: 0, finalTax: a.finalTax,
        dueDate: config.dueDate, paymentTerm: config.paymentTerm, template: config.template,
        status: 'generated' as BillStatus,
        generatedBy: 'LRA Staff', generatedDate: now, sentDate: '', paidDate: '', paidAmount: 0,
        notes: config.notes, notifChannel: config.notifChannel, notifStatus: 'queued' as NotifStatus,
        installments: buildInstallments(a.finalTax, config.paymentTerm, config.dueDate),
      };
    });
    setBills(prev => [...prev, ...newBills]);
    const newNotifs: NotifQueueItem[] = newBills.map((b, i) => ({
      id: `NQ-${String(notifQueue.length + i + 1).padStart(3, '0')}`,
      billId: b.billId, owner: b.owner, channel: config.notifChannel,
      status: 'queued' as NotifStatus,
      scheduledAt: now + ' 18:00', sentAt: '',
      message: `Tax Bill ${b.billId} for FY${b.taxYear} — Amount Due: ${fmt(b.finalTax)}. Due Date: ${config.dueDate}.`,
      attempts: 0,
    }));
    setNotifQueue(prev => [...prev, ...newNotifs]);
    setShowBatchModal(false);
    setActiveTab('bills');
  };

  const handleSend = (billId: string) => {
    setBills(prev => prev.map(b => b.billId === billId ? { ...b, status: 'sent' as BillStatus, sentDate: new Date().toISOString().split('T')[0], notifStatus: 'delivered' as NotifStatus } : b));
    setNotifQueue(prev => prev.map(n => n.billId === billId && n.status === 'queued' ? { ...n, status: 'delivered' as NotifStatus, sentAt: new Date().toISOString().split('T')[0] + ' ' + new Date().toTimeString().slice(0, 5) } : n));
  };

  const handleBatchSend = () => {
    selectedBills.forEach(id => handleSend(id));
    setSelectedBills([]);
  };

  const inputCls = 'px-3 py-2 rounded-lg text-sm outline-none focus:ring-1 focus:ring-yellow-500/40 transition-all';
  const inputStyle = { backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid var(--dark-border)', color: 'var(--dark-text)' };

  const SortIcon = ({ field }: { field: typeof sortField }) => (
    sortField === field
      ? (sortDir === 'asc' ? <ChevronUp size={11} /> : <ChevronDown size={11} />)
      : <ChevronDown size={11} style={{ opacity: 0.3 }} />
  );

  // Analytics data
  const statusData = Object.entries(BILL_STATUS_CONFIG).map(([key, cfg]) => ({
    name: cfg.label,
    value: bills.filter(b => b.status === key).length,
    color: cfg.color,
  })).filter(d => d.value > 0);

  const countyData = Array.from(new Set(bills.map(b => b.county))).map(county => ({
    county: county.length > 10 ? county.slice(0, 10) + '…' : county,
    total: bills.filter(b => b.county === county).reduce((s, b) => s + b.finalTax, 0),
    collected: bills.filter(b => b.county === county && b.status === 'paid').reduce((s, b) => s + b.paidAmount, 0),
  }));

  const TABS: { id: TabId; label: string; icon: React.ElementType }[] = [
    { id: 'bills', label: 'Bill History', icon: FileText },
    { id: 'generate', label: 'Generate Bills', icon: Receipt },
    { id: 'templates', label: 'Bill Templates', icon: Settings },
    { id: 'notifications', label: 'Notification Queue', icon: Bell },
    { id: 'analytics', label: 'Analytics', icon: BarChart2 },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--dark-bg)', color: 'var(--dark-text)' }}>
      {/* Page Header */}
      <div className="px-6 pt-6 pb-4 border-b" style={{ borderColor: 'var(--dark-border)' }}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Link href="/tax-assessment-admin" className="flex items-center gap-1 text-xs transition-colors hover:opacity-80" style={{ color: 'var(--dark-muted)' }}>
                <ArrowLeft size={12} /> Stage 5 Assessment
              </Link>
              <span style={{ color: 'var(--dark-muted)' }}>›</span>
              <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: 'rgba(212,160,23,0.15)', color: 'var(--accent)' }}>STAGE 6</span>
            </div>
            <h1 className="text-xl font-bold" style={{ color: 'var(--dark-text)' }}>Billing Dashboard</h1>
            <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>Create, manage, and dispatch tax bills from Stage 5 approved assessments</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs transition-colors hover:bg-white/5" style={{ border: '1px solid var(--dark-border)', color: 'var(--dark-muted)' }}>
              <Download size={13} /> Export
            </button>
            <button onClick={() => setShowBatchModal(true)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:opacity-90"
              style={{ backgroundColor: 'var(--accent)', color: '#000' }}>
              <Plus size={14} /> Generate Bills
            </button>
          </div>
        </div>

        {/* KPI Strip */}
        <div className="grid grid-cols-6 gap-3 mt-4">
          {[
            { label: 'Total Bills', value: kpis.total, color: '#60a5fa', icon: FileText },
            { label: 'Total Tax Billed', value: fmt(kpis.totalTax), color: 'var(--accent)', icon: DollarSign },
            { label: 'Collected', value: fmt(kpis.collected), color: '#34d399', icon: CheckCircle },
            { label: 'Pending Send', value: kpis.pending, color: '#a78bfa', icon: Clock },
            { label: 'Overdue', value: kpis.overdue, color: '#f87171', icon: AlertTriangle },
            { label: 'Notif Queue', value: kpis.queued, color: '#fbbf24', icon: Bell },
          ].map(kpi => (
            <div key={kpi.label} className="p-3 rounded-xl" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
              <div className="flex items-center gap-1.5 mb-1">
                <kpi.icon size={11} style={{ color: kpi.color }} />
                <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>{kpi.label}</span>
              </div>
              <div className="text-base font-bold" style={{ color: kpi.color }}>{kpi.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b px-6" style={{ borderColor: 'var(--dark-border)' }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className="flex items-center gap-1.5 px-4 py-3 text-xs font-medium transition-all"
            style={{
              color: activeTab === tab.id ? 'var(--accent)' : 'var(--dark-muted)',
              borderBottom: `2px solid ${activeTab === tab.id ? 'var(--accent)' : 'transparent'}`,
            }}>
            <tab.icon size={13} />
            {tab.label}
          </button>
        ))}
      </div>

      <div className="p-6">
        {/* ── Bill History Tab ── */}
        {activeTab === 'bills' && (
          <div>
            {/* Filters */}
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <div className="relative flex-1 min-w-48">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--dark-muted)' }} />
                <input className={`${inputCls} pl-8 w-full`} style={inputStyle} placeholder="Search bills, owners, parcels…" value={search} onChange={e => setSearch(e.target.value)} />
              </div>
              <select className={inputCls} style={inputStyle} value={statusFilter} onChange={e => setStatusFilter(e.target.value as BillStatus | 'all')}>
                <option value="all">All Statuses</option>
                {(Object.keys(BILL_STATUS_CONFIG) as BillStatus[]).map(s => <option key={s} value={s}>{BILL_STATUS_CONFIG[s].label}</option>)}
              </select>
              <select className={inputCls} style={inputStyle} value={countyFilter} onChange={e => setCountyFilter(e.target.value)}>
                {counties.map(c => <option key={c} value={c}>{c === 'all' ? 'All Counties' : c}</option>)}
              </select>
              {selectedBills.length > 0 && (
                <button onClick={handleBatchSend}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all"
                  style={{ backgroundColor: 'rgba(167,139,250,0.15)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.3)' }}>
                  <Send size={12} /> Send {selectedBills.length} Selected
                </button>
              )}
            </div>

            {/* Table */}
            <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--dark-border)' }}>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ backgroundColor: 'var(--dark-card)' }}>
                      <th className="px-3 py-3 text-left w-8">
                        <button onClick={toggleSelectAll}>
                          {selectedBills.length === filtered.length && filtered.length > 0
                            ? <CheckSquare size={13} style={{ color: 'var(--accent)' }} />
                            : <Square size={13} style={{ color: 'var(--dark-muted)' }} />}
                        </button>
                      </th>
                      {[
                        { label: 'Bill ID', field: 'billId' as const },
                        { label: 'Owner', field: 'owner' as const },
                        { label: 'Property', field: null },
                        { label: 'Final Tax', field: 'finalTax' as const },
                        { label: 'Due Date', field: 'dueDate' as const },
                        { label: 'Terms', field: null },
                        { label: 'Status', field: 'status' as const },
                        { label: 'Notification', field: null },
                        { label: '', field: null },
                      ].map((col, i) => (
                        <th key={i} className="px-3 py-3 text-left font-semibold" style={{ color: 'var(--dark-muted)' }}>
                          {col.field ? (
                            <button onClick={() => handleSort(col.field!)} className="flex items-center gap-1 hover:opacity-80">
                              {col.label} <SortIcon field={col.field} />
                            </button>
                          ) : col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((bill, i) => {
                      const sc = BILL_STATUS_CONFIG[bill.status];
                      const ns = bill.notifStatus ? NOTIF_STATUS_CONFIG[bill.notifStatus] : null;
                      return (
                        <tr key={bill.billId}
                          className="border-t transition-colors hover:bg-white/[0.02]"
                          style={{ borderColor: 'var(--dark-border)', backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                          <td className="px-3 py-3">
                            <button onClick={() => toggleSelect(bill.billId)}>
                              {selectedBills.includes(bill.billId)
                                ? <CheckSquare size={13} style={{ color: 'var(--accent)' }} />
                                : <Square size={13} style={{ color: 'var(--dark-muted)' }} />}
                            </button>
                          </td>
                          <td className="px-3 py-3">
                            <span className="font-mono font-semibold text-xs" style={{ color: 'var(--accent)' }}>{bill.billId}</span>
                          </td>
                          <td className="px-3 py-3">
                            <div className="font-medium" style={{ color: 'var(--dark-text)' }}>{bill.owner}</div>
                            <div style={{ color: 'var(--dark-muted)' }}>{bill.parcelId}</div>
                          </td>
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-1" style={{ color: 'var(--dark-muted)' }}>
                              {PROP_ICON[bill.propertyType]}
                              <span>{bill.propertyType}</span>
                            </div>
                            <div style={{ color: 'var(--dark-muted)' }}>{bill.county}</div>
                          </td>
                          <td className="px-3 py-3">
                            <div className="font-bold" style={{ color: '#34d399' }}>{fmt(bill.finalTax)}</div>
                            {bill.penalties > 0 && <div style={{ color: '#f87171' }}>+{fmt(bill.penalties)} penalty</div>}
                          </td>
                          <td className="px-3 py-3" style={{ color: bill.status === 'overdue' ? '#f87171' : 'var(--dark-text)' }}>
                            {bill.dueDate}
                          </td>
                          <td className="px-3 py-3">
                            <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>{PAYMENT_TERM_CONFIG[bill.paymentTerm].label}</span>
                          </td>
                          <td className="px-3 py-3">
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ color: sc.color, backgroundColor: sc.bg }}>{sc.label}</span>
                          </td>
                          <td className="px-3 py-3">
                            {ns ? (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ color: ns.color, backgroundColor: ns.bg }}>{ns.label}</span>
                            ) : <span style={{ color: 'var(--dark-muted)' }}>—</span>}
                          </td>
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-1">
                              <button onClick={() => setDetailBill(bill)}
                                className="p-1.5 rounded-lg transition-colors hover:bg-white/10"
                                style={{ color: 'var(--dark-muted)' }}>
                                <Eye size={13} />
                              </button>
                              {bill.status === 'generated' && (
                                <button onClick={() => handleSend(bill.billId)}
                                  className="p-1.5 rounded-lg transition-colors hover:bg-white/10"
                                  style={{ color: '#a78bfa' }}>
                                  <Send size={13} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={10} className="px-6 py-12 text-center text-sm" style={{ color: 'var(--dark-muted)' }}>
                          No bills match the current filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>Showing {filtered.length} of {bills.length} bills</span>
              {PENDING_ASSESSMENTS.length > 0 && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl" style={{ backgroundColor: 'rgba(212,160,23,0.06)', border: '1px solid rgba(212,160,23,0.2)' }}>
                  <AlertCircle size={12} style={{ color: 'var(--accent)' }} />
                  <span className="text-xs" style={{ color: 'var(--accent)' }}>{PENDING_ASSESSMENTS.length} Stage 5 assessments awaiting bill generation</span>
                  <button onClick={() => setShowBatchModal(true)} className="text-xs font-semibold underline" style={{ color: 'var(--accent)' }}>Generate Now</button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Generate Bills Tab ── */}
        {activeTab === 'generate' && (
          <div>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-base font-bold" style={{ color: 'var(--dark-text)' }}>Pending Stage 5 Assessments</h2>
                <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>Approved assessments ready for bill generation</p>
              </div>
              <button onClick={() => setShowBatchModal(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold"
                style={{ backgroundColor: 'var(--accent)', color: '#000' }}>
                <Receipt size={14} /> Batch Generate Bills
              </button>
            </div>
            <div className="space-y-3">
              {PENDING_ASSESSMENTS.map(a => (
                <div key={a.assessmentId} className="p-4 rounded-xl" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-sm" style={{ color: 'var(--dark-text)' }}>{a.owner}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(52,211,153,0.1)', color: '#34d399' }}>Approved</span>
                      </div>
                      <div className="grid grid-cols-4 gap-3 mt-2">
                        <div>
                          <div className="text-xs" style={{ color: 'var(--dark-muted)' }}>Assessment ID</div>
                          <div className="text-xs font-mono font-semibold mt-0.5" style={{ color: 'var(--accent)' }}>{a.assessmentId}</div>
                        </div>
                        <div>
                          <div className="text-xs" style={{ color: 'var(--dark-muted)' }}>Parcel ID</div>
                          <div className="text-xs font-medium mt-0.5" style={{ color: 'var(--dark-text)' }}>{a.parcelId}</div>
                        </div>
                        <div>
                          <div className="text-xs" style={{ color: 'var(--dark-muted)' }}>Property Type</div>
                          <div className="text-xs flex items-center gap-1 mt-0.5" style={{ color: 'var(--dark-text)' }}>{PROP_ICON[a.propertyType]} {a.propertyType}</div>
                        </div>
                        <div>
                          <div className="text-xs" style={{ color: 'var(--dark-muted)' }}>County</div>
                          <div className="text-xs font-medium mt-0.5" style={{ color: 'var(--dark-text)' }}>{a.county}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-3 mt-3">
                        <div>
                          <div className="text-xs" style={{ color: 'var(--dark-muted)' }}>Assessed Value</div>
                          <div className="text-sm font-bold mt-0.5" style={{ color: '#60a5fa' }}>{fmt(a.assessedValue)}</div>
                        </div>
                        <div>
                          <div className="text-xs" style={{ color: 'var(--dark-muted)' }}>Final Tax Due</div>
                          <div className="text-sm font-bold mt-0.5" style={{ color: '#34d399' }}>{fmt(a.finalTax)}</div>
                        </div>
                        <div>
                          <div className="text-xs" style={{ color: 'var(--dark-muted)' }}>Approved By</div>
                          <div className="text-xs font-medium mt-0.5" style={{ color: 'var(--dark-text)' }}>{a.approvedBy} · {a.approvedDate}</div>
                        </div>
                      </div>
                    </div>
                    <button onClick={() => setShowBatchModal(true)}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold flex-shrink-0 transition-all hover:opacity-90"
                      style={{ backgroundColor: 'rgba(212,160,23,0.15)', color: 'var(--accent)', border: '1px solid rgba(212,160,23,0.3)' }}>
                      <Receipt size={12} /> Generate Bill
                    </button>
                  </div>
                </div>
              ))}
              {PENDING_ASSESSMENTS.length === 0 && (
                <div className="py-16 text-center" style={{ color: 'var(--dark-muted)' }}>
                  <CheckCircle size={32} className="mx-auto mb-3 opacity-40" />
                  <p className="text-sm">All approved assessments have been billed.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Templates Tab ── */}
        {activeTab === 'templates' && (
          <div>
            <div className="mb-5">
              <h2 className="text-base font-bold" style={{ color: 'var(--dark-text)' }}>Bill Templates</h2>
              <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>Standardized LRA bill formats for different property categories</p>
            </div>
            <div className="grid grid-cols-1 gap-4">
              {BILL_TEMPLATES.map(t => (
                <div key={t.id} className="p-5 rounded-xl" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: t.headerColor, border: '1px solid rgba(255,255,255,0.1)' }}>
                      <FileText size={18} style={{ color: 'rgba(255,255,255,0.7)' }} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-sm" style={{ color: 'var(--dark-text)' }}>{t.name}</h3>
                        <span className="text-xs px-2 py-0.5 rounded-full font-mono" style={{ backgroundColor: 'rgba(212,160,23,0.1)', color: 'var(--accent)' }}>{t.id}</span>
                      </div>
                      <p className="text-xs mb-3" style={{ color: 'var(--dark-muted)' }}>{t.description}</p>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {t.propertyTypes.map(pt => (
                          <span key={pt} className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1" style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: 'var(--dark-muted)', border: '1px solid var(--dark-border)' }}>
                            {PROP_ICON[pt]} {pt}
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-3">
                        {[
                          { label: 'Map Included', active: t.includeMap },
                          { label: 'Installment Schedule', active: t.includeInstallmentSchedule },
                          { label: 'Payment Instructions', active: t.includePaymentInstructions },
                        ].map(feat => (
                          <div key={feat.label} className="flex items-center gap-1.5 text-xs" style={{ color: feat.active ? '#34d399' : 'var(--dark-muted)' }}>
                            {feat.active ? <CheckCircle size={11} /> : <X size={11} />}
                            {feat.label}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>Bills using this template</div>
                      <div className="text-xl font-bold" style={{ color: 'var(--accent)' }}>
                        {bills.filter(b => b.template === t.id).length}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Notification Queue Tab ── */}
        {activeTab === 'notifications' && (
          <div>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-base font-bold" style={{ color: 'var(--dark-text)' }}>Notification Queue</h2>
                <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>Outbound taxpayer notifications for generated bills</p>
              </div>
              <div className="flex gap-2">
                {(['queued', 'sending', 'delivered', 'failed', 'bounced'] as NotifStatus[]).map(s => {
                  const count = notifQueue.filter(n => n.status === s).length;
                  if (!count) return null;
                  const cfg = NOTIF_STATUS_CONFIG[s];
                  return (
                    <span key={s} className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ color: cfg.color, backgroundColor: cfg.bg }}>
                      {count} {cfg.label}
                    </span>
                  );
                })}
              </div>
            </div>
            <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--dark-border)' }}>
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ backgroundColor: 'var(--dark-card)' }}>
                    {['Notification ID', 'Bill ID', 'Taxpayer', 'Channel', 'Status', 'Scheduled', 'Sent', 'Attempts', 'Message Preview'].map(h => (
                      <th key={h} className="px-3 py-3 text-left font-semibold" style={{ color: 'var(--dark-muted)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {notifQueue.map((n, i) => {
                    const sc = NOTIF_STATUS_CONFIG[n.status];
                    return (
                      <tr key={n.id} className="border-t transition-colors hover:bg-white/[0.02]"
                        style={{ borderColor: 'var(--dark-border)', backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                        <td className="px-3 py-3 font-mono font-semibold" style={{ color: 'var(--dark-muted)' }}>{n.id}</td>
                        <td className="px-3 py-3 font-mono font-semibold" style={{ color: 'var(--accent)' }}>{n.billId}</td>
                        <td className="px-3 py-3 font-medium" style={{ color: 'var(--dark-text)' }}>{n.owner}</td>
                        <td className="px-3 py-3 capitalize" style={{ color: 'var(--dark-muted)' }}>
                          <div className="flex items-center gap-1">
                            {n.channel === 'email' && <Mail size={11} />}
                            {n.channel === 'sms' && <Phone size={11} />}
                            {n.channel === 'portal' && <Globe size={11} />}
                            {n.channel === 'postal' && <Package size={11} />}
                            {n.channel}
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <span className="px-2 py-0.5 rounded-full font-medium" style={{ color: sc.color, backgroundColor: sc.bg }}>{sc.label}</span>
                        </td>
                        <td className="px-3 py-3" style={{ color: 'var(--dark-muted)' }}>{n.scheduledAt}</td>
                        <td className="px-3 py-3" style={{ color: 'var(--dark-muted)' }}>{n.sentAt || '—'}</td>
                        <td className="px-3 py-3 text-center" style={{ color: n.attempts >= 3 ? '#f87171' : 'var(--dark-muted)' }}>{n.attempts}</td>
                        <td className="px-3 py-3 max-w-xs truncate" style={{ color: 'var(--dark-muted)' }}>{n.message}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Analytics Tab ── */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              {/* Bill Status Distribution */}
              <div className="p-5 rounded-xl" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
                <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--dark-text)' }}>Bill Status Distribution</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={statusData} cx="50%" cy="50%" innerRadius={55} outerRadius={80} dataKey="value" paddingAngle={3}>
                      {statusData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)', borderRadius: 8, fontSize: 11 }} />
                    <Legend iconSize={8} wrapperStyle={{ fontSize: 11, color: 'var(--dark-muted)' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Tax by County */}
              <div className="p-5 rounded-xl" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
                <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--dark-text)' }}>Tax Billed vs Collected by County</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={countyData} barSize={14}>
                    <XAxis dataKey="county" tick={{ fill: 'var(--dark-muted)', fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: 'var(--dark-muted)', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v / 1000).toFixed(0)}K`} />
                    <Tooltip contentStyle={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)', borderRadius: 8, fontSize: 11 }} formatter={(v: number) => fmt(v)} />
                    <Bar dataKey="total" name="Total Billed" fill="#60a5fa" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="collected" name="Collected" fill="#34d399" radius={[3, 3, 0, 0]} />
                    <Legend iconSize={8} wrapperStyle={{ fontSize: 11, color: 'var(--dark-muted)' }} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-4">
              {[
                { label: 'Collection Rate', value: bills.length > 0 ? `${Math.round((bills.filter(b => b.status === 'paid').length / bills.length) * 100)}%` : '0%', color: '#34d399', icon: TrendingUp },
                { label: 'Avg Bill Value', value: bills.length > 0 ? fmt(Math.round(bills.reduce((s, b) => s + b.finalTax, 0) / bills.length)) : '$0', color: '#60a5fa', icon: DollarSign },
                { label: 'Disputed Bills', value: bills.filter(b => b.status === 'disputed').length, color: '#fbbf24', icon: AlertTriangle },
                { label: 'Notification Success', value: notifQueue.length > 0 ? `${Math.round((notifQueue.filter(n => n.status === 'delivered').length / notifQueue.length) * 100)}%` : '0%', color: '#a78bfa', icon: Bell },
              ].map(stat => (
                <div key={stat.label} className="p-4 rounded-xl" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <stat.icon size={14} style={{ color: stat.color }} />
                    <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>{stat.label}</span>
                  </div>
                  <div className="text-2xl font-bold" style={{ color: stat.color }}>{stat.value}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showBatchModal && (
        <BatchGenerateModal
          assessments={PENDING_ASSESSMENTS}
          onGenerate={handleGenerate}
          onClose={() => setShowBatchModal(false)}
        />
      )}
      {detailBill && (
        <BillDetailModal
          bill={detailBill}
          onClose={() => setDetailBill(null)}
          onSend={handleSend}
        />
      )}
    </div>
  );
}
